import {OptionType} from 'components/form/Select';
import {showCommunicationError, showNotFoundToast, showSuccess} from 'helpers/alerts';
import {t} from 'i18next';
import {action, computed, makeObservable, observable, runInAction} from 'mobx';
import {browserHistory, redirectTo} from 'routing/browserHistory';
import {AppRoutePaths} from 'routing/Routes';
import {ServiceActionDto, ServiceActionTypeDto} from '../../../../../PRMGApiClient';
import {Api} from '../../../../../PRMGApiClient/ApiClient';

export class ServiceState {
  @observable
  item: ServiceActionDto = {
    id: undefined,
    carName: undefined,
    carPlate: undefined,
    name: undefined,
    serviceType: undefined,
    startDateTime: undefined,
    endDateTime: undefined,
    required: undefined,
    status: undefined,
  };

  @observable
  isLoading = false;

  @observable
  serviceTypeList: Array<ServiceActionTypeDto> = [];

  constructor() {
    makeObservable(this);
  }

  @computed
  get serviceTypes() {
    return this.serviceTypeList;
  }

  @computed
  get serviceTypesAsOptions() {
    return this.serviceTypeList.map((type, indx): OptionType => ({value: type.id ?? indx, label: type.name ?? ''}));
  }

  @action
  saveItem(newItem: ServiceActionDto) {
    this.item = newItem ?? this.item;
  }

  @action
  addAttachments(files: File[]) {
    if (!this.item.id) return;

    this.isLoading = true;
    Api.ServiceAction.apiServiceActionAddAttachmentsPost({
      serviceActionId: this.item.id,
      request: files,
    })
      .then(() => {
        this.loadService(this.item.id);
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  removeAttachment(fileId: number) {
    if (!this.item.id) return;

    this.isLoading = true;
    Api.ServiceAction.apiServiceActionDeleteAttachmentDelete({
      serviceActionId: this.item.id,
      fileId,
    })
      .then(() => this.loadService(this.item.id))
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  save(payload: ServiceActionDto): void {
    this.item = payload;
    if (payload.carId) {
      payload.id ? this.updateService(payload.carId) : this.addService(payload.carId);
    }
  }

  @action
  addService(carId: number): void {
    this.isLoading = true;
    Api.ServiceAction.apiServiceActionAddPost({
      serviceActionCreateUpdateDto: {
        ...this.item,
        carId: carId,
        typeId: this.item.serviceType?.id,
      },
    })
      .then(() => {
        showSuccess(t('Service added'));
        browserHistory.back();
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  @action
  updateService(carId: number): void {
    this.isLoading = true;
    Api.ServiceAction.apiServiceActionUpdatePut({
      serviceActionCreateUpdateDto: {
        ...this.item,
        carId: carId,
        typeId: this.item.serviceType?.id,
      },
    })
      .then(() => {
        showSuccess(t('Changes saved'));
        browserHistory.back();
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }

  loadService(serviceId: number | undefined) {
    if (!serviceId) return;

    Api.ServiceAction.apiServiceActionGetIdGet({
      id: serviceId,
    })
      .then((res) => {
        if (res.content) {
          runInAction(() => {
            this.item = {
              ...res.content,
            };
          });
        } else {
          showNotFoundToast();
          setTimeout(() => redirectTo(AppRoutePaths.services.url), 1000);
        }
      })
      .catch(showCommunicationError);
  }

  loadServiceTypeList(): void {
    Api.ServiceAction.apiServiceActionGetTypesGet()
      .then((res) => {
        runInAction(() => {
          this.serviceTypeList = res.content ?? this.serviceTypeList;
        });
      })
      .catch(showCommunicationError);
  }

  @action
  deleteService() {
    if (!this.item.id) return;

    this.isLoading = true;
    Api.ServiceAction.apiServiceActionDeleteIdDelete({
      id: this.item.id,
    })
      .then(() => {
        showSuccess(t('Service deleted'));
        browserHistory.go(-2);
      })
      .catch(showCommunicationError)
      .finally(() => (this.isLoading = false));
  }
}
